// Standalone Froala editor served from editor.bizzey.com and embedded via
// <iframe> by hosts that can't legally load Froala on their own origin (see
// widget/tourBuilder/EmbeddedEditorFrame.tsx) - our Froala license is
// domain-locked to bizzey.com, so this page is the only place non-bizzey.com
// pages are allowed to render the editor from.
import 'froala-editor/js/froala_editor.pkgd.min.js'
import 'froala-editor/js/plugins/align.min.js'
import 'froala-editor/js/plugins/font_family.min.js'
import 'froala-editor/js/plugins/font_size.min.js'
import 'froala-editor/js/plugins/colors.min.js'
import 'froala-editor/js/plugins/fullscreen.min.js'
import 'froala-editor/js/plugins/link.min.js'
import 'froala-editor/js/plugins/lists.min.js'
import 'froala-editor/js/plugins/line_breaker.min.js'
import 'froala-editor/js/plugins/paragraph_format.min.js'
import 'froala-editor/js/plugins/quote.min.js'
import 'froala-editor/js/plugins/table.min.js'
import 'froala-editor/js/plugins/url.min.js'
import 'froala-editor/js/plugins/word_paste.min.js'
import '../components/Editor/css'

import * as React from 'react'
import FroalaEditor from 'react-froala-wysiwyg'
import { FROALA_KEY } from '../Constants'
import { EDITOR_MESSAGE_SOURCE, EditorToHostMessage, HOST_MESSAGE_SOURCE, HostToEditorMessage } from './protocol'

// Deliberately doesn't reuse components/Editor/Editor.tsx: that file pulls
// in the main app's Redux store for the auth header used by image uploads,
// which doesn't exist in this standalone, unauthenticated page. No
// upload-capable buttons are enabled here as a result.
const BASE_CONFIG = {
  key: FROALA_KEY,
  editorClass: 'text-editor',
  zIndex: 'initial',
  attribution: false,
  quickInsertEnabled: false,
  pastePlain: true,
  charCounterCount: false,
  heightMin: 80,
  toolbarButtons: ['bold', 'italic', 'underline', 'strikeThrough', 'formatOL', 'formatUL', 'insertLink', 'clearFormatting'],
}

// Everything in the query string is merged straight into the Froala config,
// e.g. ?toolbarInline=true -> { toolbarInline: true }, ?heightMin=120 ->
// { heightMin: 120 }, ?toolbarButtons=bold,italic -> { toolbarButtons: [...] }.
const parseQueryConfig = (search: string): Record<string, unknown> => {
  const params = new URLSearchParams(search)
  const config: Record<string, unknown> = {}

  params.forEach((value, key) => {
    if (key === 'toolbarButtons') {
      config[key] = value.split(',').map((button) => button.trim()).filter(Boolean)
    } else if (value === 'true' || value === 'false') {
      config[key] = value === 'true'
    } else if (value !== '' && !Number.isNaN(Number(value))) {
      config[key] = Number(value)
    } else {
      config[key] = value
    }
  })

  return config
}

const postToHost = (message: EditorToHostMessage) => {
  window.parent.postMessage(message, '*')
}

const Application = () => {
  const [model, setModel] = React.useState('')
  // The host only learns about content changes after it has sent us its
  // initial value - otherwise Froala's own mount-time onModelChange would
  // immediately echo the empty string back and clobber whatever the host has.
  const receivedInitialContent = React.useRef(false)

  const config = React.useMemo(() => ({
    ...BASE_CONFIG,
    ...parseQueryConfig(window.location.search),
  }), [])

  React.useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      const data = event.data as HostToEditorMessage
      if (!data || data.source !== HOST_MESSAGE_SOURCE) return

      if (data.type === 'setContent') {
        receivedInitialContent.current = true
        setModel(data.html || '')
      }
    }

    window.addEventListener('message', onMessage)
    postToHost({ source: EDITOR_MESSAGE_SOURCE, type: 'ready' })

    return () => window.removeEventListener('message', onMessage)
  }, [])

  const onModelChange = (html: string) => {
    setModel(html)
    if (receivedInitialContent.current) postToHost({ source: EDITOR_MESSAGE_SOURCE, type: 'change', html })
  }

  return (
    <FroalaEditor model={model} onModelChange={onModelChange} config={config} />
  )
}

export default Application
